import React, {
  useCallback, useContext, useMemo, useState
} from 'react';

import getUnixTime from 'date-fns/getUnixTime';

import { RELATIVE_TIME_RANGES } from '../../../constants/time.constants';

import { getTimeRange } from '../../../utils/date_utils';

import useLocalStorage from '../../../utils/hooks/use_local_storage';

export const getStepSize = (timeRangeStartDate, timeRangeEndDate) => {
  const timeRangeSeconds = (getUnixTime(timeRangeEndDate) - getUnixTime(timeRangeStartDate))
  const timeRangeHours = timeRangeSeconds / 60 / 60;
  return timeRangeHours < 6
    ? 10
    : timeRangeHours < 12
      ? 20
      : timeRangeHours < 24
        ? 30
        : timeRangeHours < 48
          ? 60
          : timeRangeHours < 96
            ? 120
            : 600;
}

export const getTimeRangeQueryObject = (timeRangeStartDate, timeRangeEndDate) => {
  const startTimestamp = getUnixTime(timeRangeStartDate);
  const endTimestamp = getUnixTime(timeRangeEndDate);
  const step = getStepSize(timeRangeStartDate, timeRangeEndDate)

  return {
    step, startTimestamp, endTimestamp
  }
}

export const getPollIntervalByStep = step => step < 20 ? 1000 : 6000

const StaticTimeRangeContext = React.createContext({
  relativeRanges: RELATIVE_TIME_RANGES,
  setSelectedTimeRange: () => { },
  saveDefaultTimeRange: () => { },
});

// {start: Date, end: Date, type: "custom"}
export const StaticTimeRangeProvider = ({
  children,
  defaultTimeRange = null,
}) => {
  const [savedTimeRange, saveDefaultTimeRange] = useLocalStorage('static-metrics-time-range', RELATIVE_TIME_RANGES[9].type);
  const [selectedTimeRange, setSelectedTimeRange] = useState(defaultTimeRange ?? RELATIVE_TIME_RANGES.find(({ type }) => savedTimeRange === type));

  const [timeRangeStartDate, timeRangeEndDate] = useMemo(() => getTimeRange(selectedTimeRange), [selectedTimeRange]);
  const timeRangeQueryObj = useMemo(() => getTimeRangeQueryObject(timeRangeStartDate, timeRangeEndDate), [timeRangeStartDate, timeRangeEndDate]);

  const onTimeRangeChange = useCallback((newTimeRange) => {
    setSelectedTimeRange(newTimeRange);
    if (newTimeRange?.type !== 'custom') {
      saveDefaultTimeRange(newTimeRange?.type);
    }
  }, [saveDefaultTimeRange, setSelectedTimeRange])

  const timeRangeContextValue = useMemo(() => ({
    relativeRanges: RELATIVE_TIME_RANGES,
    startTimestamp: timeRangeQueryObj?.startTimestamp,
    endTimestamp: timeRangeQueryObj?.endTimestamp,
    step: timeRangeQueryObj?.step,
    timeRangeStartDate,
    timeRangeEndDate,
    selectedTimeRange,
    onTimeRangeChange,
  }), [
    timeRangeQueryObj,
    timeRangeStartDate,
    timeRangeEndDate,
    selectedTimeRange,
    onTimeRangeChange,
  ])

  return (
    <StaticTimeRangeContext.Provider
      value={timeRangeContextValue}
    >
      {children}
    </StaticTimeRangeContext.Provider>
  )
}

export const useStaticTimeRange = () => useContext(StaticTimeRangeContext);

export default useStaticTimeRange;
